import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
    return dateString;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const ApplyResignation = () => {
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [employeeInfo, setEmployeeInfo] = useState({
    employeeName: '',
    employeeId: '',
    employeeMongoId: '',
    department: ''
  });
  const [formData, setFormData] = useState({
    reason: '',
    resignationDate: '',
    personalEmail: '',
    personalNumber: '',
    declaration: false
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const mongoId = localStorage.getItem('id') || '';
    setEmployeeInfo({
      employeeName: localStorage.getItem('name') || '',
      employeeId: '',
      employeeMongoId: mongoId,
      department: localStorage.getItem('departmentName') || ''
    });
    fetchProfile(mongoId);
  }, []);

  const fetchProfile = async (mongoId) => {
    if (!mongoId || !API_URL) {
      setLoadingProfile(false);
      return;
    }

    setLoadingProfile(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'get-profile',
        id: mongoId
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      const result = response.data;
      if (result?.success && result?.profile) {
        const profile = result.profile;
        setEmployeeInfo(prev => ({
          employeeName: profile.name || profile.fullName || prev.employeeName,
          employeeId: profile.employeeId || prev.employeeId,
          employeeMongoId: mongoId,
          department: profile.departmentName || profile.department || prev.department
        }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, personalNumber: value }));
    if (errors.personalNumber) {
      setErrors(prev => ({ ...prev, personalNumber: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!employeeInfo.employeeMongoId) {
      toast.error('User ID not found. Please login again.');
      return false;
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    if (!formData.resignationDate) {
      newErrors.resignationDate = 'Resignation date is required';
    }

    if (!formData.personalEmail.trim()) {
      newErrors.personalEmail = 'Personal email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalEmail.trim())) {
      newErrors.personalEmail = 'Enter a valid email address';
    }

    if (!formData.personalNumber.trim()) {
      newErrors.personalNumber = 'Personal number is required';
    } else if (formData.personalNumber.length !== 10) {
      newErrors.personalNumber = 'Enter a valid 10-digit mobile number';
    }

    if (!formData.declaration) {
      newErrors.declaration = 'You must accept the declaration to proceed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'create-resignation',
        employeeName: employeeInfo.employeeName.trim(),
        employeeId: employeeInfo.employeeId.trim(),
        employeeMongoId: employeeInfo.employeeMongoId,
        department: employeeInfo.department.trim(),
        reason: formData.reason.trim(),
        resignationDate: formatDateToDDMMYYYY(formData.resignationDate),
        personalEmail: formData.personalEmail.trim(),
        personalNumber: formData.personalNumber.trim(),
        declaration: formData.declaration
      };

      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Resignation application submitted successfully');
        setFormData({
          reason: '',
          resignationDate: '',
          personalEmail: '',
          personalNumber: '',
          declaration: false
        });
      } else {
        toast.error(result?.message || 'Failed to submit resignation application');
      }
    } catch (error) {
      console.error('Error submitting resignation:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <h1 className="employee-title">
                Apply Resignation
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Submit your resignation request with contact details for post-employment communication.
              </p>
            </div>
          </div>

          <div className="card employee-content-card">
            <div className="card-body">
              {loadingProfile ? (
                <Loading size={80} />
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">Employee Information</h4>
                    <p className="section-subtitle">
                      Your employment details are pre-filled from your profile.
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label className="form-label">Employee Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={employeeInfo.employeeName}
                          readOnly
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Employee ID</label>
                        <input
                          type="text"
                          className="form-control"
                          value={employeeInfo.employeeId || '-'}
                          readOnly
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Department</label>
                        <input
                          type="text"
                          className="form-control"
                          value={employeeInfo.department || '-'}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="resignationDate" className="form-label">
                          Resignation Date<span className="required-star">*</span>
                        </label>
                        <input
                          type="date"
                          id="resignationDate"
                          name="resignationDate"
                          className={`form-control ${errors.resignationDate ? 'is-invalid' : ''}`}
                          value={formData.resignationDate}
                          onChange={handleChange}
                        />
                        {errors.resignationDate && (
                          <div className="invalid-feedback">{errors.resignationDate}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="personalEmail" className="form-label">
                          Personal Email<span className="required-star">*</span>
                        </label>
                        <input
                          type="email"
                          id="personalEmail"
                          name="personalEmail"
                          className={`form-control ${errors.personalEmail ? 'is-invalid' : ''}`}
                          value={formData.personalEmail}
                          onChange={handleChange}
                          placeholder="john@personal.com"
                        />
                        {errors.personalEmail && (
                          <div className="invalid-feedback">{errors.personalEmail}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="personalNumber" className="form-label">
                          Personal Number<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="personalNumber"
                          name="personalNumber"
                          className={`form-control ${errors.personalNumber ? 'is-invalid' : ''}`}
                          value={formData.personalNumber}
                          onChange={handleMobileChange}
                          placeholder="9876543210"
                          maxLength={10}
                        />
                        {errors.personalNumber && (
                          <div className="invalid-feedback">{errors.personalNumber}</div>
                        )}
                      </div>

                    </div>


                    <div className="form-group">
                      <label htmlFor="reason" className="form-label">
                        Reason<span className="required-star">*</span>
                      </label>
                      <textarea
                        id="reason"
                        name="reason"
                        className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                        value={formData.reason}
                        onChange={handleChange}
                        placeholder="Enter reason for resignation"
                        rows="4"
                      />
                      {errors.reason && (
                        <div className="invalid-feedback">{errors.reason}</div>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="declaration"
                        name="declaration"
                        className={`form-check-input ${errors.declaration ? 'is-invalid' : ''}`}
                        checked={formData.declaration}
                        onChange={handleChange}
                      />
                      <label htmlFor="declaration" className="form-check-label">
                        I hereby declare that the information provided in this resignation application is true and correct.
                        <span className="required-star">*</span>
                      </label>
                      {errors.declaration && (
                        <div className="invalid-feedback d-block">{errors.declaration}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Submitting...' : 'Submit Resignation'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplyResignation;
